/**
 * A CARD'S OWN FILES — the attachments fold on the opened card (dossier v3 §7).
 *
 * Replaces the dropped card-level RAID: somewhere to keep the artefacts a card is
 * actually about — a screenshot, a spec, a log — on the card itself. The bytes
 * live in the same private bucket the documents use, under a `card-<id>` key
 * (store.attachCardFile / migration 0223); this component shows the list, hands a
 * file up to be attached, issues a short-lived address to read one back, previews
 * it, and removes one. It owns its own fold and its own busy/problem state;
 * persistence is entirely the store's.
 *
 * PREVIEW (option C). An image shows a thumbnail and opens a full lightbox
 * (CardFileLightbox); a PDF, CSV or text file opens an inline reader in place
 * (the same `.doc-viewer` the Documents feature uses); anything else is
 * download-only. Reuses the document file-row (`.doc-file`), so a card's files
 * read exactly like the project's Documents.
 *
 * SAFETY. Names and any text content are rendered as TEXT (React escapes them;
 * text files go in a <pre>, never innerHTML). The bucket's allow-list excludes
 * SVG, so no uploaded file can carry script that runs when opened. Addresses are
 * short-lived and fetched on demand; downloads open with `noreferrer`.
 */
import { useEffect, useState } from 'react';
import type { CardAttachment, Issue } from '../types';
import { useApp } from '../store';
import { confirmDialog } from '../confirm';
import { useSampleInert } from './SampleStrip';
import { parseCsv, type Sheet } from '../import-csv';
import Icon from './Icon';
import Expander from './Expander';
import { CardFileLightbox } from './CardFileLightbox';

type ViewMode = 'image' | 'pdf' | 'table' | 'text' | null;

/** How a file can be previewed on the card, from its type (and name as a fallback). */
function viewModeOf(type: string, name: string): ViewMode {
  const n = name.toLowerCase();
  // Raster images only — SVG is not an allowed upload (it can carry script).
  if (type === 'image/png' || type === 'image/jpeg' || type === 'image/gif' || type === 'image/webp')
    return 'image';
  if (type === 'application/pdf' || n.endsWith('.pdf')) return 'pdf';
  if (type === 'text/csv' || n.endsWith('.csv')) return 'table';
  if (type === 'text/plain' || type === 'application/json' || n.endsWith('.txt') || n.endsWith('.json'))
    return 'text';
  return null;
}

/** "2.4 MB" / "640 KB" — a file size a person reads at a glance. */
function fmtSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** A short, human word for the file type, from its extension — "PDF", "PNG". */
function kindWord(name: string, type: string): string {
  const ext = name.includes('.') ? name.split('.').pop()!.toUpperCase() : '';
  if (ext) return ext;
  return type.split('/').pop()?.toUpperCase() ?? 'File';
}

/** A capped CSV preview — a glance at what was attached, not the whole sheet. */
function CsvPreview({ sheet }: { sheet: Sheet }) {
  const CAP = 100;
  const shown = sheet.rows.slice(0, CAP);
  return (
    <div className="doc-view-table">
      <table>
        <thead>
          <tr>
            {sheet.headers.map((h, i) => (
              <th key={i}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map((r, i) => (
            <tr key={i}>
              {sheet.headers.map((_, c) => (
                <td key={c}>{r[c] ?? ''}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {sheet.rows.length > CAP && (
        <p className="doc-view-more">
          Showing the first {CAP} of {sheet.rows.length} rows — download the file for the rest.
        </p>
      )}
    </div>
  );
}

/** One file row — the thumbnail/eye affordance depends on what the file is. */
function FileRow({
  file,
  issueId,
  canEdit,
  onOpenImage,
  onRemove,
}: {
  file: CardAttachment;
  issueId: string;
  canEdit: boolean;
  onOpenImage: () => void;
  onRemove: () => void;
}) {
  const urlFor = useApp((s) => s.cardFileUrl);
  const mode = viewModeOf(file.type, file.name);
  const [thumb, setThumb] = useState('');
  const [reading, setReading] = useState(false);
  const [readerUrl, setReaderUrl] = useState('');
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [text, setText] = useState('');
  const [problem, setProblem] = useState('');

  // An image fetches a thumbnail address once; the full view is the lightbox.
  useEffect(() => {
    if (mode !== 'image') return;
    let stale = false;
    void urlFor(issueId, file.id).then((u) => {
      if (!stale) setThumb(u);
    });
    return () => {
      stale = true;
    };
  }, [mode, issueId, file.id, urlFor]);

  // The inline reader (PDF/CSV/text) fetches its address when it is opened, and a
  // CSV or text file then fetches and parses its own contents.
  useEffect(() => {
    if (!reading || mode === 'image' || !mode) return;
    let stale = false;
    setProblem('');
    void urlFor(issueId, file.id).then((u) => {
      if (stale) return;
      if (!u) {
        setProblem('That file could not be opened. Try again.');
        return;
      }
      setReaderUrl(u);
      if (mode === 'table' || mode === 'text') {
        void fetch(u)
          .then((r) => r.text())
          .then((t) => {
            if (stale) return;
            if (mode === 'table') setSheet(parseCsv(t));
            else setText(t);
          })
          .catch(() => {
            if (!stale) setProblem('That file could not be read here. Download it to open it.');
          });
      }
    });
    return () => {
      stale = true;
    };
  }, [reading, mode, issueId, file.id, urlFor]);

  const download = async () => {
    const u = await urlFor(issueId, file.id);
    if (u) window.open(u, '_blank', 'noreferrer');
    else setProblem('That file could not be opened. Try again.');
  };

  return (
    <div className="doc-file">
      <div className="doc-file-line">
        {mode === 'image' && (
          <button
            type="button"
            className="cf-thumb"
            onClick={onOpenImage}
            aria-label={`View ${file.name}`}
            title="View"
          >
            {thumb ? <img src={thumb} alt="" /> : <span className="cf-thumb-wait" aria-hidden="true" />}
          </button>
        )}
        <span className="doc-file-name">{file.name}</span>
        <span className="doc-file-meta">{`${kindWord(file.name, file.type)} · ${fmtSize(file.size)}`}</span>
        <span className="doc-file-acts">
          {mode && mode !== 'image' && (
            <button
              type="button"
              className="btn-icon icon-act"
              title={reading ? 'Close it' : 'Read it here'}
              aria-label={reading ? 'Close it' : 'Read it here'}
              aria-pressed={reading}
              onClick={() => setReading((r) => !r)}
            >
              <Icon name={reading ? 'close' : 'eye'} />
            </button>
          )}
          <button
            type="button"
            className="btn-icon icon-act"
            title="Download to open it"
            aria-label={`Download ${file.name}`}
            onClick={() => void download()}
          >
            <Icon name="download" />
          </button>
          {canEdit && (
            <button
              type="button"
              className="btn-icon icon-act"
              title="Remove this file"
              aria-label={`Remove ${file.name}`}
              onClick={onRemove}
            >
              <Icon name="trash" />
            </button>
          )}
        </span>
      </div>

      {reading && mode && mode !== 'image' && (
        <div className="doc-viewer">
          {problem ? (
            <p className="doc-file-warn">{problem}</p>
          ) : !readerUrl ? (
            <p className="doc-file-warn">Fetching it…</p>
          ) : mode === 'pdf' ? (
            <object data={readerUrl} type="application/pdf" aria-label={file.name}>
              <p>
                This browser will not show a PDF here.{' '}
                <a href={readerUrl} target="_blank" rel="noreferrer">
                  Open it in a new tab
                </a>
                .
              </p>
            </object>
          ) : mode === 'table' ? (
            sheet ? (
              <CsvPreview sheet={sheet} />
            ) : (
              <p className="doc-file-warn">Reading it…</p>
            )
          ) : (
            <pre className="cf-textview">{text}</pre>
          )}
        </div>
      )}
    </div>
  );
}

export function CardFiles({ issue, canEdit }: { issue: Issue; canEdit: boolean }) {
  const attach = useApp((s) => s.attachCardFile);
  const remove = useApp((s) => s.removeCardFile);
  const urlFor = useApp((s) => s.cardFileUrl);
  const inert = useSampleInert();

  const files = issue.attachments ?? [];
  const images = files.filter((f) => viewModeOf(f.type, f.name) === 'image');
  const total = files.reduce((n, f) => n + (f.size || 0), 0);

  // Folded by default like every other section of the read view — the glance ("3
  // files · 2.4 MB", or "No files yet") carries the state, and one click opens the
  // list or the drop-zone.
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  // The index (within `images`) the lightbox is showing, or null when it is shut.
  const [lightbox, setLightbox] = useState<number | null>(null);

  const take = async (chosen: File | undefined) => {
    if (!chosen) return;
    setBusy(true);
    setProblem('');
    const why = await attach(issue.id, chosen);
    setBusy(false);
    if (why) setProblem(why);
  };

  const drop = async (attId: string, name: string) => {
    const ok = await confirmDialog({
      title: 'Remove this file?',
      body: `“${name}” will be taken off this card. This cannot be undone.`,
      confirmLabel: 'Remove file',
      cancelLabel: 'Keep it',
      tone: 'danger',
    });
    if (!ok) return;
    setProblem('');
    const why = await remove(issue.id, attId);
    if (why) setProblem(why);
  };

  const glance =
    files.length === 0
      ? 'No files yet'
      : `${files.length} file${files.length === 1 ? '' : 's'} · ${fmtSize(total)}`;

  return (
    <div className="calm-sec">
      <button type="button" className="calm-sec-h" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className={`chev${open ? ' is-open' : ''}`} aria-hidden="true">
          <Icon name="chevron" size={18} />
        </span>
        <span>Files</span>
        <span className="cnt">{glance}</span>
      </button>
      <Expander open={open}>
        <div className="card-files">
          {files.map((f) => (
            <FileRow
              key={f.id}
              file={f}
              issueId={issue.id}
              canEdit={canEdit}
              onOpenImage={() => {
                const i = images.findIndex((im) => im.id === f.id);
                if (i >= 0) setLightbox(i);
              }}
              onRemove={() => void drop(f.id, f.name)}
            />
          ))}

          {canEdit ? (
            <label {...inert('doc-drop card-file-drop', () => {})}>
              <strong>{busy ? 'Attaching…' : 'Add a file'}</strong>
              Any common file — PDF, image, Word, Excel, text — up to 25 MB.
              <input
                type="file"
                aria-label="Add a file to this card"
                disabled={busy}
                onChange={(e) => {
                  void take(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>
          ) : (
            files.length === 0 && <p className="doc-none-yet">No files have been added to this card.</p>
          )}

          {problem && (
            <p className="hintline" role="alert">
              {problem}
            </p>
          )}
        </div>
      </Expander>

      {lightbox != null && images[lightbox] && (
        <CardFileLightbox
          images={images}
          index={lightbox}
          onIndex={setLightbox}
          onClose={() => setLightbox(null)}
          urlFor={(attId) => urlFor(issue.id, attId)}
        />
      )}
    </div>
  );
}
